import type { BitcoinTransactionInput } from '@txviz/core';

/**
 * Known Bitcoin script types for display purposes.
 */
export type ScriptType =
  | 'p2pkh'
  | 'p2sh'
  | 'p2wpkh'
  | 'p2wsh'
  | 'p2tr'
  | 'op_return'
  | 'unknown';

/**
 * Detect the script type from a scriptPubKey hex.
 */
export function detectScriptType(scriptPubKey: string): ScriptType {
  const script = scriptPubKey.startsWith('0x') ? scriptPubKey.slice(2) : scriptPubKey;

  // P2PKH: OP_DUP OP_HASH160 <20 bytes> OP_EQUALVERIFY OP_CHECKSIG
  // Hex: 76a914{20 bytes}88ac
  if (script.length === 50 && script.startsWith('76a914') && script.endsWith('88ac')) {
    return 'p2pkh';
  }

  // P2SH: OP_HASH160 <20 bytes> OP_EQUAL
  // Hex: a914{20 bytes}87
  if (script.length === 46 && script.startsWith('a914') && script.endsWith('87')) {
    return 'p2sh';
  }

  // P2WPKH: OP_0 <20 bytes>
  // Hex: 0014{20 bytes}
  if (script.length === 44 && script.startsWith('0014')) {
    return 'p2wpkh';
  }

  // P2WSH: OP_0 <32 bytes>
  // Hex: 0020{32 bytes}
  if (script.length === 68 && script.startsWith('0020')) {
    return 'p2wsh';
  }

  // P2TR: OP_1 <32 bytes>
  // Hex: 5120{32 bytes}
  if (script.length === 68 && script.startsWith('5120')) {
    return 'p2tr';
  }

  // OP_RETURN
  if (script.startsWith('6a')) {
    return 'op_return';
  }

  return 'unknown';
}

/**
 * Check if a Bitcoin transaction has witness data.
 */
export function hasWitness(tx: BitcoinTransactionInput): boolean {
  return tx.inputs.some(
    (input) => input.witness !== undefined && input.witness.length > 0,
  );
}

// ── Address derivation from scriptPubKey ───────────────────────

/**
 * Derive a Bitcoin address from a scriptPubKey hex string.
 * Returns null if the script type is unsupported.
 *
 * Supports:
 * - P2WPKH (bc1q...): OP_0 <20 bytes> → bech32 witness v0
 * - P2WSH  (bc1q...): OP_0 <32 bytes> → bech32 witness v0
 * - P2TR   (bc1p...): OP_1 <32 bytes> → bech32m witness v1
 * - P2PKH  (1...):    base58check with version 0x00
 * - P2SH   (3...):    base58check with version 0x05
 */
export function deriveAddress(scriptPubKey: string): string | null {
  const script = scriptPubKey.startsWith('0x') ? scriptPubKey.slice(2) : scriptPubKey;
  const type = detectScriptType(script);

  switch (type) {
    case 'p2wpkh': {
      const hash = hexStringToBytes(script.slice(4));
      return bech32Encode('bc', 0, hash);
    }
    case 'p2wsh': {
      const hash = hexStringToBytes(script.slice(4));
      return bech32Encode('bc', 0, hash);
    }
    case 'p2tr': {
      const pubkey = hexStringToBytes(script.slice(4));
      return bech32mEncode('bc', 1, pubkey);
    }
    case 'p2pkh': {
      const hash = hexStringToBytes(script.slice(6, 46));
      return base58CheckEncode(0x00, hash);
    }
    case 'p2sh': {
      const hash = hexStringToBytes(script.slice(4, 44));
      return base58CheckEncode(0x05, hash);
    }
    default:
      return null;
  }
}

// ── Bech32 encoding (BIP-173 / BIP-350) ───────────────────────

const BECH32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function bech32Polymod(values: number[]): number {
  const GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const v of values) {
    const b = chk >> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) {
      chk ^= ((b >> i) & 1) ? GEN[i]! : 0;
    }
  }
  return chk;
}

function bech32HrpExpand(hrp: string): number[] {
  const ret: number[] = [];
  for (let i = 0; i < hrp.length; i++) {
    ret.push(hrp.charCodeAt(i) >> 5);
  }
  ret.push(0);
  for (let i = 0; i < hrp.length; i++) {
    ret.push(hrp.charCodeAt(i) & 31);
  }
  return ret;
}

function bech32CreateChecksum(hrp: string, data: number[], isBech32m: boolean): number[] {
  const values = [...bech32HrpExpand(hrp), ...data, 0, 0, 0, 0, 0, 0];
  const constant = isBech32m ? 0x2bc830a3 : 1;
  const polymod = bech32Polymod(values) ^ constant;
  const checksum: number[] = [];
  for (let i = 0; i < 6; i++) {
    checksum.push((polymod >> (5 * (5 - i))) & 31);
  }
  return checksum;
}

function convertBits(data: Uint8Array, fromBits: number, toBits: number, pad: boolean): number[] {
  let acc = 0;
  let bits = 0;
  const ret: number[] = [];
  const maxv = (1 << toBits) - 1;
  for (let i = 0; i < data.length; i++) {
    acc = (acc << fromBits) | (data[i] ?? 0);
    bits += fromBits;
    while (bits >= toBits) {
      bits -= toBits;
      ret.push((acc >> bits) & maxv);
    }
  }
  if (pad && bits > 0) {
    ret.push((acc << (toBits - bits)) & maxv);
  }
  return ret;
}

function bech32Encode(hrp: string, witnessVersion: number, data: Uint8Array): string {
  const fiveBit = convertBits(data, 8, 5, true);
  const payload = [witnessVersion, ...fiveBit];
  const checksum = bech32CreateChecksum(hrp, payload, false);
  return hrp + '1' + [...payload, ...checksum].map((d) => BECH32_CHARSET[d]).join('');
}

function bech32mEncode(hrp: string, witnessVersion: number, data: Uint8Array): string {
  const fiveBit = convertBits(data, 8, 5, true);
  const payload = [witnessVersion, ...fiveBit];
  const checksum = bech32CreateChecksum(hrp, payload, true);
  return hrp + '1' + [...payload, ...checksum].map((d) => BECH32_CHARSET[d]).join('');
}

// ── Base58Check encoding (for P2PKH / P2SH) ───────────────────

const BASE58_CHARS = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function base58CheckEncode(version: number, payload: Uint8Array): string {
  const data = new Uint8Array(1 + payload.length);
  data[0] = version;
  data.set(payload, 1);

  // Double SHA-256 for checksum
  let checksum: Uint8Array;
  try {
    // Node.js sync path
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const c = require('node:crypto') as {
      createHash(alg: string): { update(d: Uint8Array): { digest(): Buffer } };
    };
    const h1 = c.createHash('sha256').update(data).digest();
    const h2 = c.createHash('sha256').update(h1).digest();
    checksum = new Uint8Array(h2.slice(0, 4));
  } catch {
    // Browser: return without checksum (bech32 addresses are preferred anyway)
    return base58EncodeBytes(data);
  }

  const full = new Uint8Array(data.length + 4);
  full.set(data);
  full.set(checksum, data.length);
  return base58EncodeBytes(full);
}

function base58EncodeBytes(bytes: Uint8Array): string {
  const digits: number[] = [0];
  for (let i = 0; i < bytes.length; i++) {
    let carry = bytes[i] ?? 0;
    for (let j = 0; j < digits.length; j++) {
      carry += (digits[j] ?? 0) << 8;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  let leading = '';
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === 0) leading += '1';
    else break;
  }
  while (digits.length > 1 && digits[digits.length - 1] === 0) digits.pop();
  if (digits.length === 1 && digits[0] === 0 && leading.length > 0) return leading;
  return leading + digits.reverse().map((d) => BASE58_CHARS[d]).join('');
}

function hexStringToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}
